import React, { useMemo } from 'react';
import { FormField } from '@src/components/SmartDataTable/types';
import { DELIVERY_STATUS_OPTIONS, PAYMENT_STATUS_OPTIONS } from '../constants';
import { formatNumber } from '@src/utils/numberUtils';
import dayjs from 'dayjs';
import { getPhoneFieldConfig } from '@src/utils/phoneUtils';

// Types for purchase order item calculations
interface Medicine {
  id: number;
  name: string;
  hsn: string;
  manufacturer: string;
  pack_size: string;
  pack_quantity: number;
  mrp: number;
  gst_rate: number;
}

interface PurchaseOrderItem {
  id: string | number;
  medicine_id?: number;
  medicine?: any;
  hsn?: string;
  mfg?: string;
  pack_size?: string;
  pack_quantity?: number;
  quantity?: number;
  discount_quantity?: number;
  received_quantity?: number;
  mrp?: number;
  rate?: number;
  amount?: number;
  gst_rate?: number;
  gst_amount?: number;
  discount?: number;
  net_amount?: number;
  delivery_status?: string;
  rate_manually_changed?: boolean; // Track if rate was manually changed
  [key: string]: any;
}

// Pack quantity parser function
const parsePackQuantity = (packSizeStr: string): number => {
  if (!packSizeStr) return 1;

  packSizeStr = packSizeStr.toLowerCase().trim();

  // Case: "10 x 10" or "1*12" or "1 * 12" or "1x12"
  const matchMultiply = packSizeStr.match(/(\d+)\s*[x*]\s*(\d+)/);
  if (matchMultiply) {
    return parseInt(matchMultiply[1]) * parseInt(matchMultiply[2]);
  }

  // Case: Combo kit like (2+1+3)
  const matchCombo = packSizeStr.match(/^\(?\s*\d+(?:\s*\+\s*\d+)+\s*\)?$/);
  if (matchCombo) {
    // strip any parens and spaces, then sum the numbers
    const total = matchCombo[0]
      .replace(/[()\ ]/g, '') // remove ( ), spaces
      .split('+')
      .map(Number)
      .reduce((a, b) => a + b, 0);
    return total;
  }

  // Case: Units like cap, tab, sachet
  const matchUnit = packSizeStr.match(
    /^(\d+)\s*(cap|tab|sachet|vial|ampoule)$/
  );
  if (matchUnit) {
    return parseInt(matchUnit[1]);
  }

  // Case: Weight/Volume like 100 gm or 30 ml – count as 1 pack
  const matchPack = packSizeStr.match(/^(\d+)\s*(gm|ml|kg|l)$/);
  if (matchPack) {
    return 1;
  }

  // Case: Just a number
  const matchNumber = packSizeStr.match(/^(\d+)$/);
  if (matchNumber) {
    return parseInt(matchNumber[1]);
  }

  // Default fallback
  return 1;
};

export const usePurchaseOrdersFormFields = (): ((
  record?: any
) => FormField[]) => {
  // Add debugging at the hook level

  // Common calculation functions for purchase order items
  const calculateAmount = (rate: number, quantity: number): number => {
    return formatNumber(rate * quantity);
  };

  const calculateGSTAmount = (amount: number, gstRate: number): number => {
    return formatNumber((amount * gstRate) / 100);
  };

  const calculateDiscountAmount = (
    amount: number,
    discount: number
  ): number => {
    return formatNumber((amount * discount) / 100);
  };

  const calculateNetAmount = (
    amount: number,
    gstAmount: number,
    discountAmount: number
  ): number => {
    return formatNumber(amount + gstAmount - discountAmount);
  };

  // Helper function to force recalculate received_quantity for delivered items
  const forceRecalculateReceivedQuantity = (
    item: PurchaseOrderItem
  ): PurchaseOrderItem => {
    const updatedItem = { ...item };

    // Force recalculate received_quantity if delivery status is DELIVERED
    if (updatedItem.delivery_status === 'DELIVERED') {
      const quantity = formatNumber(updatedItem.quantity || 0);
      const discountQuantity = formatNumber(updatedItem.discount_quantity || 0);
      const packQuantity = formatNumber(updatedItem.pack_quantity || 1);
      const newReceivedQuantity = formatNumber(
        (quantity + discountQuantity) * packQuantity
      );

      // Always update received_quantity for delivered items
      updatedItem.received_quantity = newReceivedQuantity;
    }

    return updatedItem;
  };

  // Unified calculation function for a purchase order item
  const calculatePurchaseOrderItemTotals = (
    item: PurchaseOrderItem
  ): PurchaseOrderItem => {
    // Use the actual rate from the item (what's shown in the UI)
    const rate = formatNumber(item.rate || 0);
    const quantity = formatNumber(item.quantity || 0);
    const discount = formatNumber(item.discount || 0);
    const gstRate = formatNumber(item.gst_rate || 0);

    // Calculate base amount (Rate × Quantity)
    const amount = calculateAmount(rate, quantity);

    // Calculate GST amount on base amount
    const gstAmount = calculateGSTAmount(amount, gstRate);

    // Calculate discount amount on base amount
    const discountAmount = calculateDiscountAmount(amount, discount);

    // Calculate net amount (amount + GST - discount)
    const netAmount = calculateNetAmount(amount, gstAmount, discountAmount);

    return {
      ...item,
      amount,
      gst_amount: gstAmount,
      net_amount: netAmount,
    };
  };

  // Helper function to calculate overall delivery status based on item statuses
  const calculateOverallDeliveryStatus = (
    purchase_order_items: PurchaseOrderItem[]
  ): string => {
    // Filter out deleted items
    const activeItems = purchase_order_items.filter(
      (item: any) => item._action !== 'delete'
    );

    if (activeItems.length === 0) {
      return 'PENDING'; // Default status for empty orders
    }

    // Count items by delivery status
    const statusCounts = activeItems.reduce(
      (counts, item) => {
        const status = item.delivery_status || 'PENDING';
        counts[status] = (counts[status] || 0) + 1;
        return counts;
      },
      {} as Record<string, number>
    );

    const totalItems = activeItems.length;

    const deliveredCount = statusCounts['DELIVERED'] || 0;

    // Logic for overall status
    if (deliveredCount === totalItems) {
      return 'DELIVERED'; // All items delivered
    } else {
      return 'PENDING'; // All items pending or mixed with pending
    }
  };

  // Helper function to calculate purchase order totals
  const calculatePurchaseOrderTotals = (
    purchase_order_items: PurchaseOrderItem[],
    form: any
  ) => {
    try {
      // Filter out deleted items from calculations
      const activeItems = purchase_order_items.filter(
        (item: any) => item._action !== 'delete'
      );

      // Calculate subtotal (sum of all amounts before GST and discount)
      const subtotal = activeItems.reduce(
        (sum, item) => sum + formatNumber(item.amount || 0),
        0
      );

      // Calculate total discount amount
      const totalDiscount = activeItems.reduce((sum, item) => {
        const amount = formatNumber(item.amount || 0);
        const discount = formatNumber(item.discount || 0);
        return sum + calculateDiscountAmount(amount, discount);
      }, 0);

      // Calculate total GST amount
      const totalTax = activeItems.reduce(
        (sum, item) => sum + formatNumber(item.gst_amount || 0),
        0
      );

      // Calculate total amount: subtotal + total tax - total discount
      const totalAmount = formatNumber(subtotal + totalTax - totalDiscount);

      // Calculate overall delivery status based on item statuses
      const overallDeliveryStatus =
        calculateOverallDeliveryStatus(purchase_order_items);

      // Get current form values to compare
      const currentValues = form?.getFieldsValue?.() || {};
      const newValues = {
        subtotal: formatNumber(subtotal),
        discount_amount: formatNumber(totalDiscount),
        tax_amount: formatNumber(totalTax),
        total_amount: totalAmount,
        due_amount: totalAmount,
        delivery_status: overallDeliveryStatus, // Add overall delivery status
      };

      // Check if values have actually changed (even more strict comparison)
      const hasChanged = Object.keys(newValues).some(key => {
        const currentValue = currentValues[key];
        const newValue = (newValues as any)[key];

        // Handle string comparisons (like delivery_status)
        if (typeof newValue === 'string') {
          return currentValue !== newValue;
        }

        // Handle numeric comparisons
        const currentNumValue = Number(currentValue || 0).toFixed(2);
        const newNumValue = Number(newValue || 0).toFixed(2);
        const changed = currentNumValue !== newNumValue;

        return changed;
      });

      if (!hasChanged) {
        return; // Skip update if nothing changed
      }

      // Update form fields - use only setFieldsValue to prevent conflicts
      if (form && typeof form.setFieldsValue === 'function') {
        form.setFieldsValue(newValues);
      }
    } catch (error) {
      // Silent error handling
    }
  };

  // Common onValueChange handler for all columns
  const handleColumnValueChange = (
    columnKey: string,
    value: any,
    rowData: PurchaseOrderItem,
    allRows: PurchaseOrderItem[],
    form: any
  ) => {
    let updatedRow = { ...rowData };

    // Always check for actual value changes to prevent infinite loops
    // BUT allow received_quantity calculation fields to always process
    const calculationTriggerFields = [
      'medicine_id',
      'pack_size',
      'rate',
      'quantity',
      'discount_quantity',
      'gst_rate',
      'discount',
      'delivery_status',
    ];

    const shouldSkipChangeCheck = calculationTriggerFields.includes(columnKey);

    if (!shouldSkipChangeCheck) {
      const currentValue = rowData[columnKey];
      const formattedCurrentValue =
        typeof currentValue === 'number'
          ? formatNumber(currentValue)
          : currentValue;
      const formattedNewValue =
        typeof value === 'number' ? formatNumber(value || 0) : value;

      if (formattedCurrentValue === formattedNewValue) {
        return; // Skip if no actual change
      }
    } else {
    }

    switch (columnKey) {
      case 'medicine_id':
        // Handle medicine selection - always process, no early returns

        if (value && value.data) {
          // Extract medicine data from AutoComplete response
          const medicine = value.data;
          const medicineId = medicine.id; // Get ID from the medicine object

          if (medicine && medicineId) {
            // Process medicine data with safety checks
            const processedData = {
              id: medicineId,
              name: medicine.name || '',
              hsn: medicine.hsn || '',
              manufacturer: medicine.manufacturer || '',
              pack_size: medicine.pack_size || '',
              pack_quantity: medicine.pack_quantity || 1,
              mrp: medicine.mrp || 0,
              gst_rate: medicine.gst_rate || 0,
            };

            // Update row with medicine data
            updatedRow = {
              ...rowData,
              medicine_id: medicineId,
              medicine: processedData,

              // Update visible fields
              hsn: processedData.hsn,
              mfg: processedData.manufacturer,
              pack_size: processedData.pack_size,
              pack_quantity: processedData.pack_quantity,
              mrp: processedData.mrp,
              rate: processedData.mrp, // Default rate to MRP
              quantity: 1, // Default quantity
              gst_rate: processedData.gst_rate,

              // Reset other fields
              discount: 0,
              discount_quantity: 0,
              received_quantity: 0,
              delivery_status: 'PENDING',

              // Track that rate is not manually changed (using MRP)
              rate_manually_changed: false,
            };
          } else {
            return; // Exit early if data is invalid
          }
        } else if (value === null || value === undefined) {
          // Clear fields when medicine is deselected
          updatedRow = {
            ...rowData,
            medicine_id: undefined,
            medicine: null,
            hsn: '',
            mfg: '',
            pack_size: '',
            pack_quantity: 0,
            mrp: 0,
            rate: 0,
            quantity: 0,
            gst_rate: 0,
            discount: 0,
            discount_quantity: 0,
            received_quantity: 0,
            delivery_status: 'PENDING',
          };
        } else {
          return; // Exit early for unexpected formats
        }
        break;

      case 'pack_size':
        // Handle pack_size changes and auto-calculate pack_quantity
        updatedRow[columnKey] = value || '';

        // Auto-calculate pack_quantity using the parser
        if (value && typeof value === 'string') {
          const calculatedPackQuantity = parsePackQuantity(value);
          updatedRow.pack_quantity = calculatedPackQuantity;
        } else {
          // If pack_size is cleared, reset pack_quantity to 1
          updatedRow.pack_quantity = 1;
        }
        break;

      case 'quantity':
        // Update quantity and keep current rate logic
        updatedRow[columnKey] = formatNumber(value || 0);
        // Only update rate if it hasn't been manually changed and we have MRP
        if (
          !updatedRow.rate_manually_changed &&
          updatedRow.mrp &&
          updatedRow.mrp > 0
        ) {
          updatedRow.rate = updatedRow.mrp;
        }

        // Auto-calculate received_quantity if delivery status is DELIVERED
        if (updatedRow.delivery_status === 'DELIVERED') {
          const quantity = formatNumber(value || 0);
          const discountQuantity = formatNumber(
            updatedRow.discount_quantity || 0
          );
          const packQuantity = formatNumber(updatedRow.pack_quantity || 1);
          updatedRow.received_quantity = formatNumber(
            (quantity + discountQuantity) * packQuantity
          );
        }
        break;

      case 'rate':
        // Handle rate changes - mark as manually changed
        updatedRow[columnKey] = formatNumber(value || 0);
        updatedRow.rate_manually_changed = true;

        break;

      case 'gst_rate':
        // Handle GST rate changes
        updatedRow[columnKey] = formatNumber(value || 0);

        break;

      case 'discount':
        // Update discount field
        updatedRow[columnKey] = formatNumber(value || 0);
        break;

      case 'discount_quantity':
        // Update discount_quantity and auto-calculate received_quantity if delivered
        updatedRow[columnKey] = formatNumber(value || 0);
        // Auto-calculate received_quantity if delivery status is DELIVERED
        if (updatedRow.delivery_status === 'DELIVERED') {
          const quantity = formatNumber(updatedRow.quantity || 0);
          const discountQuantity = formatNumber(value || 0);
          const packQuantity = formatNumber(updatedRow.pack_quantity || 1);
          updatedRow.received_quantity = formatNumber(
            (quantity + discountQuantity) * packQuantity
          );
        }
        break;

      case 'received_quantity':
        // Update received_quantity field
        updatedRow[columnKey] = formatNumber(value || 0);
        break;

      case 'delivery_status':
        // Update delivery status and auto-calculate received_quantity if DELIVERED
        updatedRow[columnKey] = value;
        // Auto-calculate received_quantity if status is DELIVERED
        if (value === 'DELIVERED') {
          const quantity = formatNumber(updatedRow.quantity || 0);
          const discountQuantity = formatNumber(
            updatedRow.discount_quantity || 0
          );
          const packQuantity = formatNumber(updatedRow.pack_quantity || 1);
          updatedRow.received_quantity = formatNumber(
            (quantity + discountQuantity) * packQuantity
          );
        }
        break;

      default:
        // For other fields, just update the value
        updatedRow[columnKey] = value;
        break;
    }

    // Force recalculate received_quantity for delivered items
    updatedRow = forceRecalculateReceivedQuantity(updatedRow);

    // Recalculate totals for the updated row
    updatedRow = calculatePurchaseOrderItemTotals(updatedRow);

    // Update the row in the table immediately
    const rowIndex = allRows.findIndex(row => row.id === rowData.id);
    if (rowIndex !== -1) {
      allRows[rowIndex] = updatedRow;

      // Use setTimeout to debounce and prevent infinite loops
      setTimeout(() => {
        try {
          // Update the form field
          form.setFields([
            { name: 'purchase_order_items', value: [...allRows] },
          ]);

          // Recalculate totals after a brief delay
          setTimeout(() => {
            calculatePurchaseOrderTotals(allRows, form);
          }, 10);
        } catch (error) {
          // Silent error handling
        }
      }, 50); // 50ms debounce to prevent rapid updates
    }
  };

  return useMemo(
    () => (record?: any) => [
      // Row 1: Invoice No, Supplier, Notes
      {
        name: 'invoice_no',
        label: 'Invoice No',
        type: 'text',
        required: false,
        placeholder: 'Enter invoice number (optional)',
        layout: { span: 8 },
      },
      {
        name: 'supplier_id',
        label: 'Supplier',
        type: 'resourceSelect',
        required: false,
        placeholder: 'Search and select supplier...',
        searchSelectProps: {
          resource: 'suppliers',
          optionValue: 'id',
          searchFields: ['name', 'contact_person', 'contact_no', 'phone'],
          optionRender: (item: any) => (
            <div>
              <span>{item.name}</span>
              <span> (+{item.phone})</span>
            </div>
          ),
          allowClear: true,
        },
        layout: { span: 8 },
        defaultValue: record?.supplier?.id || record?.supplier_id || null,
        props: {
          validateTrigger: ['onBlur', 'onChange'],
          validateOnMount: false,
          validateFirst: false,
        },
        onChange: (value: any, form: any) => {
          // Convert undefined/empty to null for database and clear referred_by_name when type is cleared
          if (value === undefined || value === '') {
            form.setFieldsValue({ referred_by_type: null });
            form.setFieldsValue({ referred_by_name: null });
          }
        },
      },
      {
        name: 'notes',
        label: 'Notes',
        type: 'text',
        required: false,
        placeholder: 'Enter any notes...',
        layout: { span: 8 },
      },

      // Row 2: Order Date, Expected Delivery Date, Actual Delivery Date
      {
        name: 'order_date',
        label: 'Order Date',
        type: 'datetime',
        defaultValue: (() => {
          const now = dayjs();
          return now.second(0).millisecond(0);
        })(),
        props: {
          showShortcuts: false,
          dateFormat: 'DD-MM-YYYY hh:mm A',
        },
        required: true,
        layout: { span: 8 },
      },
      {
        name: 'expected_delivery_date',
        label: 'Expected Delivery Date',
        type: 'datetime',
        defaultValue: (() => {
          const now = dayjs();
          return now.add(2, 'day').second(0).millisecond(0);
        })(),
        props: {
          showShortcuts: false,
          dateFormat: 'DD-MM-YYYY hh:mm A',
        },
        required: true,
        layout: { span: 8 },
      },
      {
        name: 'actual_delivery_date',
        label: 'Actual Delivery Date',
        type: 'datetime',
        props: {
          showShortcuts: false,
          dateFormat: 'DD-MM-YYYY hh:mm A',
        },
        required: false,
        layout: { span: 8 },
      },

      // Row 3: Manufacturer Contact Person, Manufacturer Phone
      {
        name: 'manufacturer_id',
        label: 'MR/Contact Person',
        type: 'resourceSelect',
        required: false,
        placeholder: 'Search and select manufacturer...',
        searchSelectProps: {
          resource: 'manufacturers',
          optionValue: 'id',
          searchFields: ['manufacturer', 'contact_person', 'phone'],
          optionRender: (item: any) => (
            <div>
              <span>{item.contact_person}</span>
              <span> (+{item.phone})</span>
            </div>
          ),
          allowClear: true,
        },
        layout: { span: 8 },
        defaultValue: record?.manufacturer?.id || record?.manufacturer_id,
        props: {
          validateTrigger: ['onBlur', 'onChange'],
          validateOnMount: false,
          validateFirst: false,
        },
        onChange: (value: any, form: any) => {
          if (value?.data) {
            const manufacturer = value.data;

            const contactPerson = manufacturer.contact_person || '';
            const phone = manufacturer.phone.substring(2) || '';

            if (form && typeof form.setFieldsValue === 'function') {
              form.setFieldsValue({
                manufacturer_contact_person: contactPerson,
                manufacturer_phone: phone,
              });
            }
          } else {
            if (form && typeof form.setFieldsValue === 'function') {
              form.setFieldsValue({
                manufacturer_contact_person: '',
                manufacturer_phone: '',
              });
            }
          }
        },
      },
      {
        name: 'manufacturer_contact_person',
        label: 'Contact Person Name',
        type: 'text',
        required: true,
        placeholder:
          'Contact person will auto-fill when manufacturer is selected',
        layout: { span: 8 },
      },
      {
        name: 'manufacturer_phone',
        label: 'Contact Person Phone',
        ...getPhoneFieldConfig(true, '98765 43210'),
        required: false,
        props: {
          ...getPhoneFieldConfig().props,
          size: 'middle',
          className: 'compact-phone-field',
        },
        rules: [{ required: false, message: 'Please enter phone number' }],
        layout: { span: 8 },
        tooltip: 'Enter 10-digit mobile number (without country code)',
      },

      // Row 4: Purchase Order Items Table
      {
        name: 'purchase_order_items', // CHANGED: Use API field name instead of snake_case
        label: 'Purchase Order Items',
        type: 'table',
        required: true,
        tableProps: {
          title: '',
          addButtonText: 'Add Medicine',
          minRows: 1,
          showRowNumbers: true,
          showAddButton: true,
          addButtonPosition: 'footer',
          summaryRow: {
            enabled: true,
            fields: ['amount', 'net_amount'],
          },
          columns: [
            {
              key: 'medicine',
              title: 'Medicine',
              dataIndex: 'medicine_id',
              width: 200,
              maxWidth: 200,
              field: {
                name: 'medicine_id',
                label: 'Medicine',
                type: 'resourceAutoComplete',
                required: true,
                placeholder: 'Select medicine...',
                autoCompleteProps: {
                  resource: 'medicines',
                  optionValue: 'id',
                  searchFields: ['name', 'manufacturer', 'hsn'],
                  optionRender: (option: any) => {
                    if (!option) return '';
                    return `${option.name}`;
                  },
                },
              },
              onValueChange: (value, rowData, allRows, form) => {
                handleColumnValueChange(
                  'medicine_id',
                  value,
                  rowData,
                  allRows,
                  form
                );
              },
            },
            {
              key: 'delivery_status',
              title: 'Status',
              dataIndex: 'delivery_status',
              width: 150,
              field: {
                name: 'delivery_status',
                label: 'Status',
                type: 'select',
                required: true,
                defaultValue: 'PENDING',
                options: [...DELIVERY_STATUS_OPTIONS],
              },
              onValueChange: (value, rowData, allRows, form) => {
                handleColumnValueChange(
                  'delivery_status',
                  value,
                  rowData,
                  allRows,
                  form
                );
              },
            },
            {
              key: 'hsn',
              title: 'HSN',
              dataIndex: 'hsn',
              width: 120,
              field: {
                name: 'hsn',
                label: 'HSN',
                type: 'text',
                disabled: true,
              },
            },
            {
              key: 'mfg',
              title: 'Manufacturer',
              dataIndex: 'mfg',
              width: 120,
              field: {
                name: 'mfg',
                label: 'Manufacturer',
                type: 'text',
                disabled: true,
              },
            },
            {
              key: 'pack_size',
              title: 'Pack Size',
              dataIndex: 'pack_size',
              width: 120,
              field: {
                name: 'pack_size',
                label: 'Pack Size',
                type: 'text',
                disabled: false, // Make editable to allow manual entry
                placeholder: 'e.g., 10x10, 12 cap, (2+1)',
                tooltip:
                  'Enter pack size to auto-calculate quantity. Examples: "10x10", "12 cap", "(2+1+3)"',
              },
              onValueChange: (value, rowData, allRows, form) => {
                handleColumnValueChange(
                  'pack_size',
                  value,
                  rowData,
                  allRows,
                  form
                );
              },
            },
            {
              key: 'pack_quantity',
              title: 'Pack Qty',
              dataIndex: 'pack_quantity',
              width: 80,
              field: {
                name: 'pack_quantity',
                label: 'Pack Qty',
                type: 'number',
                disabled: true,
                align: 'right',
              },
            },
            {
              key: 'mrp',
              title: 'MRP',
              dataIndex: 'mrp',
              width: 100,
              field: {
                name: 'mrp',
                label: 'MRP',
                type: 'number',
                format: 'currency',
                decimalPlaces: 2,
                disabled: true,
              },
            },
            {
              key: 'quantity',
              title: 'Quantity',
              dataIndex: 'quantity',
              width: 80,
              field: {
                name: 'quantity',
                label: 'Quantity',
                type: 'number',
                required: true,
                validation: { min: 1 },
                placeholder: '1',
                align: 'right',
              },
              onValueChange: (value, rowData, allRows, form) => {
                handleColumnValueChange(
                  'quantity',
                  value,
                  rowData,
                  allRows,
                  form
                );
              },
            },
            {
              key: 'discount_quantity',
              title: 'Free Qty.',
              dataIndex: 'discount_quantity',
              width: 80,
              field: {
                name: 'discount_quantity',
                label: 'Free',
                type: 'number',
                required: false,
                defaultValue: 0,
                validation: { min: 0 },
                align: 'right',
              },
              onValueChange: (value, rowData, allRows, form) => {
                handleColumnValueChange(
                  'discount_quantity',
                  value,
                  rowData,
                  allRows,
                  form
                );
              },
            },
            {
              key: 'received_quantity',
              title: 'Received',
              dataIndex: 'received_quantity',
              width: 80,
              field: {
                name: 'received_quantity',
                label: 'Received',
                type: 'number',
                required: false,
                defaultValue: 0,
                validation: { min: 0 },
                align: 'right',
              },
              onValueChange: (value, rowData, allRows, form) => {
                handleColumnValueChange(
                  'received_quantity',
                  value,
                  rowData,
                  allRows,
                  form
                );
              },
            },
            {
              key: 'batch_number',
              title: 'Batch No',
              dataIndex: 'batch_number',
              width: 100,
              field: {
                name: 'batch_number',
                label: 'Batch No',
                type: 'text',
                required: false,
                defaultValue: '',
                align: 'left',
              },
            },
            {
              key: 'expiry_date',
              title: 'Expiry Date',
              dataIndex: 'expiry_date',
              width: 100,
              field: {
                name: 'expiry_date',
                label: 'Expiry Date',
                type: 'text',
                required: false,
                defaultValue: '',
              },
            },
            {
              key: 'rate',
              title: 'Rate',
              dataIndex: 'rate',
              width: 100,
              field: {
                name: 'rate',
                label: 'Rate',
                type: 'number',
                format: 'decimal',
                decimalPlaces: 2,
                required: true,
                placeholder: '0.00',
                validation: { min: 0 },
                align: 'right',
              },
              onValueChange: (value, rowData, allRows, form) => {
                handleColumnValueChange('rate', value, rowData, allRows, form);
              },
            },
            {
              key: 'amount',
              title: 'Amount',
              dataIndex: 'amount',
              width: 100,
              field: {
                name: 'amount',
                label: 'Amount',
                type: 'number',
                format: 'decimal',
                decimalPlaces: 2,
                required: true,
                placeholder: '0.00',
                disabled: true,
                align: 'right',
              },
            },
            {
              key: 'gst_rate',
              title: 'GST %',
              dataIndex: 'gst_rate',
              width: 80,
              field: {
                name: 'gst_rate',
                label: 'GST %',
                type: 'number',
                required: false,
                placeholder: '0.00',
                defaultValue: 0,
                validation: { min: 0 },
                align: 'right',
                format: 'percentage',
              },
              onValueChange: (value, rowData, allRows, form) => {
                handleColumnValueChange(
                  'gst_rate',
                  value,
                  rowData,
                  allRows,
                  form
                );
              },
            },
            {
              key: 'gst_amount',
              title: 'GST',
              dataIndex: 'gst_amount',
              width: 100,
              field: {
                name: 'gst_amount',
                label: 'GST Amount',
                type: 'number',
                format: 'decimal',
                decimalPlaces: 2,
                required: false,
                placeholder: '0.00',
                disabled: true,
                align: 'right',
              },
            },
            {
              key: 'discount',
              title: 'Discount %',
              dataIndex: 'discount',
              width: 100,
              field: {
                name: 'discount',
                label: 'Discount %',
                type: 'number',
                format: 'percentage',
                required: false,
                defaultValue: 0,
                validation: { min: 0, max: 100 },
                align: 'right',
              },
              onValueChange: (value, rowData, allRows, form) => {
                handleColumnValueChange(
                  'discount',
                  value,
                  rowData,
                  allRows,
                  form
                );
              },
            },
            {
              key: 'net_amount',
              title: 'Net Amount',
              dataIndex: 'net_amount',
              width: 120,
              field: {
                name: 'net_amount',
                label: 'Net Amount',
                type: 'number',
                format: 'currency',
                decimalPlaces: 2,
                required: true,
                placeholder: '0.00',
                disabled: true,
                align: 'right',
              },
            },
          ],
          onSummaryChange: summary => {
            // This will be handled by the form's onSummaryChange
          },
          onRowDelete: (deletedRow: any, allRows: any[], form: any) => {
            // Immediate recalculation when a row is deleted
            setTimeout(() => {
              calculatePurchaseOrderTotals(allRows, form);
            }, 0);
          },
        },
        layout: { span: 24 },
        // Add transform functions for proper data loading in edit mode
        // IMPORTANT: The key issue is that API returns 'purchaseOrderItems' but form expects 'purchase_order_items'
        transform: {
          input: (value: any) => {
            // Handle existing data when loading for edit (simple approach like sales)
            if (Array.isArray(value) && value.length > 0) {
              // Process each item to ensure proper data structure
              const processedItems = value.map((item: any, index: number) => {
                // Ensure medicine data is properly structured
                // Check for medicine_details first (API response format), then fallback to medicine
                let medicine = item.medicine_details || item.medicine;

                if (!medicine && item.medicine_id) {
                  // If medicine object is missing but we have an ID, create a proper structure
                  medicine = {
                    id: item.medicine_id,
                    name:
                      item.medicine_name ||
                      item.name ||
                      `Medicine ID: ${item.medicine_id}`,
                    hsn: item.hsn || '',
                    manufacturer: item.mfg || item.manufacturer || '',
                    pack_size: item.pack_size || '',
                    pack_quantity: item.pack_quantity || 0,
                    mrp: item.mrp || 0,
                    gst_rate: item.gst_rate || 0,
                  };
                } else if (medicine && !medicine.id && item.medicine_id) {
                  // Ensure medicine has proper ID
                  medicine = {
                    ...medicine,
                    id: item.medicine_id,
                  };
                }

                // Calculate received_quantity for DELIVERED items if not already set
                let calculatedReceivedQuantity = formatNumber(
                  item.received_quantity || 0
                );
                const deliveryStatus = item.delivery_status || 'PENDING';
                const quantity = formatNumber(item.quantity || 1);
                const discountQuantity = formatNumber(
                  item.discount_quantity || 0
                );

                // Auto-calculate received_quantity if delivery status is DELIVERED and received_quantity is 0
                if (
                  deliveryStatus === 'DELIVERED' &&
                  calculatedReceivedQuantity === 0
                ) {
                  const packQuantity = formatNumber(
                    item.pack_quantity || medicine?.pack_quantity || 1
                  );
                  calculatedReceivedQuantity = formatNumber(
                    (quantity + discountQuantity) * packQuantity
                  );
                }

                const processedItem = {
                  ...item,
                  id: item.id || `temp_${Date.now()}_${index}`,
                  medicine: medicine,
                  // Store original medicine name for display
                  medicine_name:
                    medicine?.name || item.medicine_name || item.name,
                  // Ensure all required fields have default values
                  hsn: item.hsn || medicine?.hsn || '',
                  mfg:
                    item.mfg ||
                    item.manufacturer ||
                    medicine?.manufacturer ||
                    '',
                  pack_size: item.pack_size || medicine?.pack_size || '',
                  pack_quantity: formatNumber(
                    item.pack_quantity || medicine?.pack_quantity || 0
                  ),
                  mrp: formatNumber(item.mrp || medicine?.mrp || 0),
                  quantity: quantity,
                  discount_quantity: discountQuantity,
                  received_quantity: calculatedReceivedQuantity,
                  rate: formatNumber(
                    item.rate || item.mrp || medicine?.mrp || 0
                  ),
                  amount: formatNumber(item.amount || 0),
                  gst_rate: formatNumber(
                    item.gst_rate || medicine?.gst_rate || 0
                  ),
                  gst_amount: formatNumber(item.gst_amount || 0),
                  discount: formatNumber(item.discount || 0),
                  net_amount: formatNumber(item.net_amount || 0),
                  delivery_status: deliveryStatus,
                  // Track if rate was manually set (if different from MRP)
                  rate_manually_changed:
                    item.rate && item.mrp && item.rate !== item.mrp,
                  // Pass just the medicine ID - AutoCompleteField will fetch the data internally
                  medicine_id: medicine?.id || item.medicine_id,
                };

                return processedItem;
              });

              return processedItems;
            }
            return value || [];
          },
          output: (value: any) => {
            // Handle form submission - clean up temporary fields (matching sales approach)
            if (Array.isArray(value)) {
              return value.map((item: any) => {
                const cleanItem = { ...item };
                // Remove temporary tracking fields
                delete cleanItem.rate_manually_changed;
                // Remove temporary IDs if they exist
                if (
                  typeof cleanItem.id === 'string' &&
                  cleanItem.id.startsWith('temp_')
                ) {
                  delete cleanItem.id;
                }
                return cleanItem;
              });
            }
            return value;
          },
        },
        onChange: (value: any, form: any) => {
          // Handle purchase order items change
          form.setFieldsValue({ purchase_order_items: value });
        },
      },

      // Row 5: Financial Details Row 1
      {
        name: 'subtotal',
        label: 'Subtotal',
        type: 'number',
        required: true,
        placeholder: '0.00',
        format: 'decimal',
        decimalPlaces: 2,
        layout: { span: 6 },
      },
      {
        name: 'discount_amount',
        label: 'Discount Amount',
        type: 'number',
        required: false,
        placeholder: '0.00',
        format: 'decimal',
        decimalPlaces: 2,
        layout: { span: 6 },
      },

      // Row 6: Financial Details Row 2
      {
        name: 'tax_amount',
        label: 'Tax Amount',
        type: 'number',
        required: false,
        placeholder: '0.00',
        format: 'decimal',
        decimalPlaces: 2,
        layout: { span: 6 },
      },
      {
        name: 'total_amount',
        label: 'Total Amount',
        type: 'number',
        required: true,
        placeholder: '0.00',
        format: 'decimal',
        decimalPlaces: 2,
        layout: { span: 6 },
      },
      {
        name: 'due_amount',
        label: 'Due Amount',
        type: 'number',
        required: false,
        placeholder: '0.00',
        format: 'decimal',
        decimalPlaces: 2,
        layout: { span: 6 },
      },
      // {
      //   name: 'payment_status',
      //   label: 'Payment Status',
      //   type: 'radio',
      //   required: true,
      //   defaultValue: 'PENDING',
      //   options: [...PAYMENT_STATUS_OPTIONS],
      //   layout: { span: 6 },
      //   props: {
      //     buttonStyle: 'solid',
      //     size: 'middle',
      //   },
      // },
      // Hidden field to ensure delivery_status is included in payload
      {
        name: 'delivery_status',
        label: 'Delivery Status',
        type: 'text',
        defaultValue: 'PENDING',
        hidden: true, // This will make the field hidden in the UI
        layout: { span: 0 }, // Take no space in the layout
      },
    ],
    []
  );
};
